import { Controller } from "@hotwired/stimulus"

// Enhances a normal multipart file input; file contents are still validated by
// the server. Selecting/dropping a file never submits the form automatically.
export class FileDropController extends Controller {
    static targets = ["input", "zone", "filename", "error"]
    static values = { maxBytes: Number }

    connect() {
        this.resetDrag()
        this.changed()
    }

    disconnect() {
        this.resetDrag()
    }

    isFileDrag(event) {
        return Array.from(event.dataTransfer?.types ?? []).includes("Files")
    }

    enter(event) {
        if (!this.isFileDrag(event)) return
        event.preventDefault()
        this.dragDepth++
        this.zoneTarget.dataset.dragging = "true"
    }

    over(event) {
        if (!this.isFileDrag(event)) return
        event.preventDefault()
        event.dataTransfer.dropEffect = "copy"
    }

    leave(event) {
        if (!this.dragDepth) return
        event.preventDefault()
        this.dragDepth = Math.max(0, this.dragDepth - 1)
        if (!this.dragDepth) this.resetDrag()
    }

    drop(event) {
        if (!this.isFileDrag(event)) return
        event.preventDefault()
        this.resetDrag()
        const files = event.dataTransfer.files
        const problem = this.problem(files)
        if (problem) return this.reject(problem)
        try {
            this.inputTarget.files = files
        } catch {
            return this.reject("This browser could not select the dropped file. Click to choose it instead.")
        }
        this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }

    changed() {
        const files = this.inputTarget.files
        const problem = files.length ? this.problem(files) : null
        if (problem) return this.reject(problem)
        this.inputTarget.setCustomValidity("")
        this.inputTarget.removeAttribute("aria-invalid")
        this.errorTarget.textContent = ""
        this.errorTarget.hidden = true
        this.filenameTarget.textContent = files.length ? files[0].name : "or click to choose a PDF or TXT file"
    }

    problem(files) {
        if (files.length !== 1) return "Choose exactly one PDF or TXT order file."
        const file = files[0]
        if (!/\.(pdf|txt)$/i.test(file.name)) return "Choose a PDF or TXT order file. Other file types are not supported."
        if (!file.size) return "This file is empty. Choose a nonempty order file."
        if (this.maxBytesValue > 0 && file.size > this.maxBytesValue) return "This file exceeds the maximum size shown below. Choose a smaller order file."
        return null
    }

    reject(message) {
        // Do not accidentally submit an earlier valid file after a bad drop.
        this.inputTarget.value = ""
        this.inputTarget.setCustomValidity(message)
        this.inputTarget.setAttribute("aria-invalid", "true")
        this.filenameTarget.textContent = "or click to choose a PDF or TXT file"
        this.errorTarget.textContent = message
        this.errorTarget.hidden = false
    }

    resetDrag() {
        this.dragDepth = 0
        this.zoneTarget.dataset.dragging = "false"
    }
}
